import { User, BarChart2, Globe, MessageSquare, Award, MapPin, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import './CampusAmbassador.css';

const pillars = [
  {
    icon: Award,
    title: 'Learn',
    body: 'Gain advanced knowledge in quantum computing through exclusive workshops, curated resources, and direct mentorship from researchers.',
  },
  {
    icon: Globe,
    title: 'Connect',
    body: 'Build lasting relationships with quantum experts, researchers, and fellow ambassadors across institutions and borders.',
  },
  {
    icon: BarChart2,
    title: 'Grow',
    body: 'Develop leadership, communication, and advocacy skills while shaping the future of quantum education on your campus.',
  },
];

const responsibilities = [
  {
    icon: MessageSquare,
    title: 'Organise Events',
    body: 'Host workshops, study groups, and awareness seminars that bring quantum science to life for your peers.',
  },
  {
    icon: User,
    title: 'Recruit Members',
    body: 'Champion the community by inviting peers to join, participate in programmes, and attend events.',
  },
  {
    icon: Award,
    title: 'Represent',
    body: 'Act as the official face of QuantumKE at your institution — at local events, conferences, and in your academic network.',
  },
  {
    icon: BarChart2,
    title: 'Provide Feedback',
    body: 'Share ground-level insights on what learners need and help us continuously improve our educational offerings.',
  },
];

function CampusAmbassadorProgram() {
  return (
    <div className="ca-page">
      <Helmet>
        <title>Campus Ambassador Program | QuantumKE</title>
        <meta name="description" content="Represent QuantumKE on your campus. The Campus Ambassador Program helps students learn, connect, and grow as advocates for quantum science and technology across Kenyan universities." />
        <link rel="canonical" href="https://quantumke.org/ambassadors" />
      </Helmet>

      {/* ── HERO ── */}
      <section className="ca-hero">
        <div className="ca-hero-overlay" />
        <div className="ca-hero-inner">
          <span className="ca-label">Engagement Programme</span>
          <h1 className="ca-hero-title">Campus Ambassador Program</h1>
          <p className="ca-hero-sub">
            Become a quantum advocate at your institution and help build the future of quantum technology education across Africa.
          </p>
          <a
            href="https://forms.gle/pd4vKdB4qV75oULn9"
            target="_blank"
            rel="noopener noreferrer"
            className="ca-btn-primary"
          >
            Apply Now <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* ── PILLARS ── */}
      <section className="ca-section">
        <div className="ca-container">
          <div className="ca-section-head">
            <span className="ca-label">Programme Overview</span>
            <h2 className="ca-section-title">What You Gain</h2>
            <p className="ca-section-desc">
              As a QuantumKE Campus Ambassador you are not just spreading awareness — you are building real skills, a global network, and a track record in quantum.
            </p>
          </div>
          <div className="ca-pillars-grid">
            {pillars.map(({ icon: Icon, title, body }) => (
              <div key={title} className="ca-pillar-card">
                <div className="ca-pillar-icon">
                  <Icon size={22} />
                </div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RESPONSIBILITIES ── */}
      <section className="ca-section">
        <div className="ca-container">
          <div className="ca-section-head">
            <span className="ca-label">Your Role</span>
            <h2 className="ca-section-title">Ambassador Responsibilities</h2>
            <p className="ca-section-desc">
              Ambassadors are active contributors to the QuantumKE mission, not passive representatives.
            </p>
          </div>
          <div className="ca-resp-grid">
            {responsibilities.map(({ icon: Icon, title, body }) => (
              <div key={title} className="ca-resp-card">
                <div className="ca-resp-icon">
                  <Icon size={20} />
                </div>
                <div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CAMPUS TOURS ── */}
      <section className="ca-section ca-tours-section">
        <div className="ca-container">
          <div className="ca-tours-card">
            <div className="ca-tours-glow" aria-hidden />
            <div className="ca-tours-content">
              <div className="ca-tours-icon-wrap">
                <MapPin size={28} />
              </div>
              <div>
                <span className="ca-label">Campus Outreach</span>
                <h2 className="ca-section-title" style={{ textAlign: 'left', marginBottom: '12px' }}>
                  We Come to You
                </h2>
                <p className="ca-tours-body">
                  QuantumKE runs dedicated <strong>campus tours</strong> — visiting universities and colleges across Kenya to deliver talks, interactive demos, and introductory sessions on quantum science and technology. These visits are led by our team and ambassadors, bringing quantum directly into lecture halls, science clubs, and innovation hubs.
                </p>
                <p className="ca-tours-body">
                  As an ambassador, you are the key contact who helps us coordinate visits to your institution — making quantum education a living, physical presence on your campus rather than just an online offering.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default CampusAmbassadorProgram;
